import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import pt from "./locales/pt.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import pl from "./locales/pl.json";
import fr from "./locales/fr.json";
import { isBR } from "@/lib/market";
import { toPtBR } from "./ptBR";

/* -------------------------------------------------------------------------- */
/*                               SUPPORTED LANGS                              */
/* -------------------------------------------------------------------------- */

export const SUPPORTED_LANGUAGES = [
  { code: "pt", name: "Português", flag: "🇵🇹", nativeName: "Português" },
  { code: "en", name: "English", flag: "🇬🇧", nativeName: "English" },
  { code: "es", name: "Español", flag: "🇪🇸", nativeName: "Español" },
  { code: "pl", name: "Polski", flag: "🇵🇱", nativeName: "Polski" },
  { code: "fr", name: "Français", flag: "🇫🇷", nativeName: "Français" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: SupportedLanguage = "pt";

/* -------------------------------------------------------------------------- */
/*                                 URL PATHS                                  */
/* -------------------------------------------------------------------------- */

export const URL_PATHS: Record<SupportedLanguage, Record<string, string>> = {
  pt: {
    equipment: "equipamentos",
    consumables: "consumiveis",
    cart: "carrinho",
    checkout: "checkout",
    account: "conta",
    support: "assistencia",
    contact: "contacto",
    about: "sobre",
    blog: "blog",
    solutions: "solucoes",
    financing: "financiamento",
    install: "instalar",
    terms: "termos-condicoes",
    order: "encomenda",
    shop: "loja",
  },
  en: {
    equipment: "equipment",
    consumables: "consumables",
    cart: "cart",
    checkout: "checkout",
    account: "account",
    support: "support",
    contact: "contact",
    about: "about",
    blog: "blog",
    solutions: "solutions",
    financing: "financing",
    install: "install",
    terms: "terms-conditions",
    order: "order",
    shop: "shop",
  },
  es: {
    equipment: "equipos",
    consumables: "consumibles",
    cart: "carrito",
    checkout: "checkout",
    account: "cuenta",
    support: "soporte",
    contact: "contacto",
    about: "nosotros",
    blog: "blog",
    solutions: "soluciones",
    financing: "financiacion",
    install: "instalar",
    terms: "terminos-condiciones",
    order: "pedido",
    shop: "tienda",
  },
  pl: {
    equipment: "sprzet",
    consumables: "materialy-eksploatacyjne",
    cart: "koszyk",
    checkout: "zamowienie",
    account: "konto",
    support: "wsparcie",
    contact: "kontakt",
    about: "o-nas",
    blog: "blog",
    solutions: "rozwiazania",
    financing: "finansowanie",
    install: "instaluj",
    terms: "regulamin",
    order: "zamowienie",
    shop: "sklep",
  },
  fr: {
    equipment: "equipements",
    consumables: "consommables",
    cart: "panier",
    checkout: "commander",
    account: "compte",
    support: "assistance",
    contact: "contact",
    about: "a-propos",
    blog: "blog",
    solutions: "solutions",
    financing: "financement",
    install: "installer",
    terms: "conditions-generales",
    order: "commande",
    shop: "boutique",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  I18N INIT                                 */
/* -------------------------------------------------------------------------- */

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      pt: { translation: pt },
      en: { translation: en },
      es: { translation: es },
      pl: { translation: pl },
      fr: { translation: fr },
    },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    interpolation: {
      escapeValue: false,
    },
    ...(isBR ? { lng: "pt" } : {}),
    detection: {
      order: isBR ? [] : ["path", "localStorage", "navigator"],
      lookupFromPathIndex: 0,
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false,
    },
  });

if (isBR) {
  i18n.addResourceBundle("pt", "translation", toPtBR(pt), true, true);
  if (typeof document !== "undefined") document.documentElement.lang = "pt-BR";
}

export default i18n;

/* -------------------------------------------------------------------------- */
/*                             ENTERPRISE HELPERS                             */
/* -------------------------------------------------------------------------- */

/**
 * Get localized base path (adds /en, /es etc)
 */
export function getLocalizedPath(path: string, lang: SupportedLanguage): string {
  if (lang === DEFAULT_LANGUAGE) return path;
  return `/${lang}${path}`;
}

/**
 * Get language from pathname
 */
export function getLanguageFromPath(pathname: string): SupportedLanguage {
  const match = pathname.match(/^\/(en|es|pl|fr)(\/|$)/);
  return (match?.[1] as SupportedLanguage) || DEFAULT_LANGUAGE;
}

/**
 * Remove language prefix
 */
export function removeLanguagePrefix(pathname: string): string {
  return pathname.replace(/^\/(en|es|pl|fr)(\/|$)/, "/");
}

/**
 * Translate a route key (enterprise slug system)
 * Example: translateRoute('equipment', 'es')
 */
export function translateRoute(key: string, lang: SupportedLanguage): string {
  return URL_PATHS[lang]?.[key] || URL_PATHS[DEFAULT_LANGUAGE][key];
}

/**
 * Switch language while keeping same route
 */
export function switchLanguagePath(currentPath: string, targetLang: SupportedLanguage): string {
  const cleanPath = removeLanguagePrefix(currentPath);

  if (targetLang === DEFAULT_LANGUAGE) {
    return cleanPath;
  }

  return `/${targetLang}${cleanPath}`;
}
